/**
 * Bộ nhập Excel danh mục dùng chung (04/11/2026):
 * - Cột tham chiếu (khoá ngoại / danh mục cấu hình) khai `ref` trên ExcelColumnDef, định nghĩa ở ImportRefDef:
 *   khớp giá trị theo mã / tên (không phân biệt hoa thường, dấu), giá trị chưa có → hộp nhập hỏi Tạo tự động / Mở form / Bỏ dòng.
 * - runUpsertImport: mã đã có → cập nhật, chưa có → tạo; trả báo cáo từng dòng (tạo mới / cập nhật / lỗi).
 */
import { extractErrorMessage } from '@/lib/utils';

export interface ImportRefOption {
  /** Giá trị ghi vào bản ghi (id khoá ngoại, hoặc tên chuẩn của mục cấu hình) */
  value: string;
  /** Các chuỗi để khớp với ô Excel: mã, tên, nhãn… */
  keys: string[];
  label: string;
}

export interface ImportRefDef {
  /** Tên hiển thị của danh mục, VD "Nhà cung cấp" */
  label: string;
  load: () => Promise<ImportRefOption[]>;
  /** Tạo tự động từ giá trị trong file (đã cho người dùng sửa lại) → trả giá trị ghi vào bản ghi; không có = không cho tạo */
  create?: (raw: string) => Promise<string>;
  /** Mở form chuẩn của danh mục (useEntityQuickAdd) để người dùng xác nhận trước khi tạo */
  quickAddType?: string;
  /** Dữ liệu điền sẵn cho form chuẩn */
  formDefaults?: (raw: string) => Record<string, unknown>;
  /** Người dùng có quyền thêm mục mới vào danh mục này không */
  canCreate?: boolean;
  /** Lý do không tạo được (VD ĐVT chỉ Admin thêm) — hiện trong hộp nhập */
  createHint?: string;
}

export type ImportRefs = Record<string, ImportRefDef>;

export type ImportRowStatus = 'created' | 'updated' | 'failed' | 'skipped';

export interface ImportRowResult {
  row: number;
  code?: string;
  status: ImportRowStatus;
  message?: string;
}

export interface ImportResult {
  rows: ImportRowResult[];
  /** Mục danh mục tham chiếu đã tạo thêm trong lần nhập này */
  createdRefs?: { label: string; value: string }[];
}

export const ROW_KEY = '__row';

/** Chuẩn hoá để khớp: thường, bỏ dấu, gọn khoảng trắng */
export function normKey(v: unknown): string {
  return String(v ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'd')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

export function buildRefIndex(options: ImportRefOption[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const o of options) for (const k of o.keys) if (k) map.set(normKey(k), o.value);
  return map;
}

export function summarize(result: ImportResult) {
  const count = (s: ImportRowStatus) => result.rows.filter((r) => r.status === s).length;
  return { created: count('created'), updated: count('updated'), failed: count('failed'), skipped: count('skipped') };
}

/**
 * Bỏ khoá nội bộ (số dòng) và ô trống trước khi gửi máy chủ: ô trống = giữ nguyên khi cập nhật,
 * lấy giá trị mặc định khi tạo mới (không ghi đè dữ liệu đang có bằng rỗng).
 */
export function stripMeta<T extends Record<string, unknown>>(row: T): Omit<T, typeof ROW_KEY> {
  const { [ROW_KEY]: _row, ...rest } = row as any;
  for (const k of Object.keys(rest)) {
    if (rest[k] === null || rest[k] === undefined || (typeof rest[k] === 'string' && rest[k].trim() === '')) delete rest[k];
  }
  return rest;
}

/**
 * Ghi từng dòng: tìm bản ghi theo mã → update, không có → create. Lỗi một dòng không dừng cả file.
 * findExisting trả id bản ghi đã có (hoặc null).
 */
export async function runUpsertImport(
  rows: Record<string, unknown>[],
  opts: {
    codeOf: (row: Record<string, unknown>) => string;
    findExisting: (code: string, row: Record<string, unknown>) => Promise<string | null>;
    create: (data: Record<string, unknown>) => Promise<unknown>;
    update: (id: string, data: Record<string, unknown>) => Promise<unknown>;
    /** Bỏ / đổi trường trước khi ghi (VD không gửi mã khi cập nhật) */
    prepare?: (row: Record<string, unknown>, mode: 'create' | 'update') => Record<string, unknown>;
  },
): Promise<ImportResult> {
  const results: ImportRowResult[] = [];
  for (const row of rows) {
    const rowNo = Number(row[ROW_KEY]) || 0;
    const code = opts.codeOf(row);
    const data = stripMeta(row);
    try {
      const existingId = code ? await opts.findExisting(code, row) : null;
      if (existingId) {
        await opts.update(existingId, opts.prepare ? opts.prepare(data, 'update') : data);
        results.push({ row: rowNo, code, status: 'updated' });
      } else {
        await opts.create(opts.prepare ? opts.prepare(data, 'create') : data);
        results.push({ row: rowNo, code, status: 'created' });
      }
    } catch (err) {
      results.push({ row: rowNo, code, status: 'failed', message: extractErrorMessage(err) || 'Lỗi' });
    }
  }
  return { rows: results };
}

/**
 * Tìm id bản ghi theo mã: nạp danh sách một lần rồi tra trong bộ nhớ (không gọi từng mã → không lỗi 404 khi mã mới).
 * loadAll trả mảng bản ghi; codeOf lấy mã của bản ghi (so không phân biệt hoa thường).
 */
export function findByCode(loadAll: () => Promise<any>, codeOf: (rec: any) => unknown) {
  let index: Promise<Map<string, string>> | null = null;
  return async (code: string) => {
    index ??= loadAll().then((res: any) => {
      const list: any[] = Array.isArray(res) ? res : res?.items || res?.data || [];
      return new Map(list.filter((r) => codeOf(r)).map((r) => [normKey(codeOf(r)), String(r.id)]));
    });
    return (await index).get(normKey(code)) ?? null;
  };
}
